import { useScreenSize } from '@composables/useScreenSize';
import { useAuthStore } from '@/stores/auth.store';
import { useCookiesStore } from '@/stores/cookies.store';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';

// Create a singleton instance
let gaInstance = null;

/**
 * Hook for Google Analytics and Google Tag Manager integration
 * @returns {Object} Object containing analytics methods and state
 */
export const useGoogleAnalytics = () => {
  if (gaInstance) {
    return gaInstance;
  }

  const cookiesStore = useCookiesStore();
  const { userCookies } = storeToRefs(cookiesStore);

  /**
   * Initializes Google Analytics and Google Tag Manager
   * @param {Object} gaConfig - Configuration object for analytics
   * @param {string} gaConfig.mbg_ga_code - Google Analytics tracking ID for mobile
   * @param {string} gaConfig.bg_ga_code - Google Analytics tracking ID for desktop
   * @param {string} gaConfig.gtm_container_id - Google Tag Manager container ID
   * @returns {void}
   */
  const initializeAnalytics = (gaConfig) => {
    if (!gaConfig) {
      console.warn('Google Analytics configuration is required');
      return;
    }

    /*if (!userCookies.value.analyticsCookies) {
      // Do not initialize if analytics cookies are not accepted
      return;
    }*/

    const { isMobile } = useScreenSize();
    const trackingId = isMobile.value
      ? gaConfig.mbg_ga_code
      : gaConfig.bg_ga_code;
    const gtmContainerId = gaConfig.gtm_container_id;

    // Initialize dataLayer first
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    if (trackingId) {
      // Load GA4 (gtag)
      const gaScript = document.createElement('script');
      gaScript.id = 'ga-script';
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}&t=${Date.now()}`;

      gaScript.onload = () => {
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', trackingId);

        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
          analytics_storage: 'granted',
        });
      };

      document.head.appendChild(gaScript);
    }

    if (gtmContainerId) {
      // Load GTM
      const gtmScript = document.createElement('script');
      gtmScript.id = 'gtm-script';
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmContainerId}&t=${Date.now()}`;

      gtmScript.onload = () => {
        // No need to set isGtmInitialized
      };

      document.head.appendChild(gtmScript);
      if (userCookies.value.analyticsCookies) {
        // Add GTM noscript iframe
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmContainerId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
      }
    }
  };

  // Get auth store for channelKey
  const authStore = useAuthStore();

  /**
   * Ensures dataLayer exists in the window object
   * @returns {Array} The dataLayer array
   */
  const ensureDataLayer = () => {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  };

  /**
   * Tracks a page view in Google Analytics
   * @param {string} path - The page path to track
   * @param {string} [hotelId] - Optional hotel ID to prepend to the path
   * @returns {void}
   */
  const trackPageView = (path, hotelId) => {
    if (!window.gtag || !userCookies.value.analyticsCookies) return;
    window.gtag('event', 'page_view', {
      page_path: hotelId ? `${hotelId}${path}` : path,
    });
  };

  const trackBeginCheckoutEventEcommerce = (data) => {
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const items = [];
    //iterate through each data object
    data.forEach((res) => {
      const itemId = res.room_rate.room_rate_code;
      const itemName = res.room.name + ' - ' + res.rate_plan.name;
      const itemCategory = 'room';
      const itemPrice = res.room_rate.total_price;
      const itemQuantity = 1;
      items.push({
        item_id: itemId,
        item_name: itemName,
        item_category: itemCategory,
        price: itemPrice,
        quantity: itemQuantity,
      });

      const beginCheckout = {
        event: 'begin_checkout',
        currency: res.room_rate.currency,
        value: res.room_rate.total_price,
        items: items,
      };

      pushToDataLayer(beginCheckout);
    });
  };

  const trackAddPaymentInfoEventEcommerce = (data) => {
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const items = [];
    //iterate through each data object
    data.forEach((res) => {
      const itemId = res.room_rate.room_rate_code;
      const itemName = res.room.name + ' - ' + res.rate_plan.name;
      const itemCategory = 'room';
      const itemPrice = res.room_rate.total_price;
      const itemQuantity = 1;
      items.push({
        item_id: itemId,
        item_name: itemName,
        item_category: itemCategory,
        price: itemPrice,
        quantity: itemQuantity,
      });

      const beginCheckout = {
        event: 'add_payment_info',
        currency: res.room_rate.currency,
        value: res.room_rate.total_price,
        items: items,
      };

      pushToDataLayer(beginCheckout);
    });
  };

  /**
   * Tracks a booking event with both standard GTM format and enhanced ecommerce
   * @param {Array<Object>} reservations - Array of reservation objects
   * @returns {void}
   */
  const trackBookedEventEcommerce = (reservations) => {
    // Check if analytics cookies are accepted
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const route = useRoute();

    // Also track with the original format for backward compatibility
    // Iterate through each reservation and push to dataLayer
    reservations.forEach((res) => {
      // Calculate number of nights
      const checkIn = new Date(res.checkin);
      const checkOut = new Date(res.checkout);
      const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));

      //Create a new array of objects for addons
      const addons = res.addons
        ? res.addons.map((addon) => ({
            name: addon.name,
            netPriceTotal: addon.net_price,
          }))
        : [];

      //create reservation object
      const gcReservation = {
        code: res.hotel_reservation_code,
        propertyName: res.hotel.hotel_name,
        offer: res.room_type.name + ' - ' + res.rate_plan.name,
        offerId: res.room_rate.room_rate_code,
        mealPlan: res.meal_plan.name,
        checkIn: res.checkin,
        checkOut: res.checkout,
        nights: nights,
        quantity: 1,
        adults: res.number_of_adults,
        children: res.number_of_children,
        addons: res.addons || [],
      };

      //create price object
      const gcPrice = {
        currency: res.room_rate.currency,
        total: res.room_rate.total_price,
        net: res.room_rate.net_price,
        netAddons:
          addons && addons.length > 0
            ? addons.reduce((sum, addon) => sum + addon.netPriceTotal, 0)
            : 0,
        netStay: res.room_rate.net_price,
        taxes: res.room_rate.taxes,
        fees: res.room_rate.fees,
        perUnit: res.room_rate.net_price,
      };

      //create guest object
      const gcGuest = {
        firstName: res.guest.first_name,
        lastName: res.guest.last_name,
        name: `${res.guest.first_name} ${res.guest.last_name}`,
        city: res.guest.city,
        zip: res.guest.postal_code,
        country: res.guest.country,
        payment: res.credit_card ? res.credit_card.name || '' : '',
      };

      const transProducts = res.addons
        ? res.addons.map((addon) => ({
            item_id: addon.name,
            item_name: addon.name,
            item_category: 'addon',
            price: parseFloat(addon.net_price),
            quantity: addon.quantity,
          }))
        : [];

      transProducts.push({
        item_id: gcReservation.offerId,
        item_name: gcReservation.offer,
        item_category: 'room',
        price: parseFloat(gcPrice.perUnit),
        quantity: gcReservation.quantity,
      });

      //create ecommerce object
      const gcEcommerce = {
        event: 'purchase',
        transaction_id: res.hotel_reservation_code,
        affiliation: authStore.channelKey ? authStore.channelKey : '',
        value: res.room_rate.total_price,
        currency: res.room_rate.currency,
        tax: res.room_rate.taxes,
        items: transProducts,
        utm_source: route.query.utm_source || '',
        utm_medium: route.query.utm_medium || '',
        utm_campaign: route.query.utm_campaign || '',
        utm_content: route.query.utm_content || '',
      };

      const eventData = {
        event: 'booked',
        // Reservation details
        gcReservation: gcReservation,
        //price details
        gcPrice: gcPrice,
        // Guest details
        gcGuest: gcGuest,
        // Metadata fields
        gcReferer: res.booking_referer,
        gcLanguage: res.guest.language,
        gcCustomParam: res.custom_param || '',
        gcMobile: useScreenSize().isMobile ? true : false,
        gcClientId: res.hotel.hotel_id,
        gcChannelKey: authStore.channelKey ? authStore.channelKey : '',
        // ecommerce
        transactionId: res.hotel_reservation_code,
        transactionAffiliation: authStore.channelKey
          ? authStore.channelKey
          : '',
        transactionTotal: res.room_rate.total_price,
        transactionTax: res.room_rate.taxes,
        transactionShipping: '-',
        transactionProducts: transProducts,
        ecommerce: gcEcommerce,
      };

      // Push to dataLayer
      pushToDataLayer(eventData);
      pushToDataLayer(gcEcommerce);
    });
  };

  /**
   * Pushes data to the Google Tag Manager dataLayer
   * @param {Object} data - Data object to push to the dataLayer
   * @returns {void}
   */
  const pushToDataLayer = (data) => {
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const dataLayer = ensureDataLayer();
    dataLayer.push(data);
  };

  /**
   * Pushes a standard event to the Google Tag Manager dataLayer
   * @param {string} eventName - Name of the event
   * @param {Object} [eventData={}] - Additional event data
   * @returns {void}
   */
  const pushEvent = (eventName, eventData = {}) => {
    pushToDataLayer({
      event: eventName,
      ...eventData,
    });
  };

  /**
   * Clears previous ecommerce object from dataLayer to prevent data leakage
   * @returns {void}
   */
  const clearEcommerceData = () => {
    pushToDataLayer({
      ecommerce: null,
    });
  };

  /**
   * Tracks an event in both Google Analytics and Google Tag Manager
   * @param {string} eventName - Name of the event
   * @param {Object} [eventParams={}] - Additional event parameters
   * @returns {void}
   */
  const trackEvent = (eventName, eventParams = {}) => {
    // Track with GA if analytics cookies are accepted
    if (window.gtag && userCookies.value.analyticsCookies) {
      window.gtag('event', eventName, eventParams);
    }

    // Also track with GTM if analytics cookies are accepted
    if (userCookies.value.analyticsCookies) {
      pushEvent(eventName, eventParams);
    }
  };

  /**
   * Removes Google Analytics and Tag Manager scripts from the page
   * @returns {void}
   */
  const stopAnalytics = () => {
    if (userCookies.value.analyticsCookies) {
      // Do not initialize if analytics cookies are not accepted
      return;
    }
    // Remove all GA/GTM related scripts and iframes
    const ids = [
      'GTM-',
      'UA-',
      'G-',
      'gtm.js',
      'gtag/js',
      'analytics.js',
      'gtag/js',
    ];

    // Remove all scripts and iframes
    document.querySelectorAll('script, iframe').forEach((el) => {
      if (
        ids.some((id) => el.src?.includes(id)) ||
        el.innerText?.includes('gtag') ||
        el.innerText?.includes('dataLayer') ||
        el.innerText?.includes('analytics')
      ) {
        el.remove();
      }
    });

    // Remove GTM noscript iframe
    const gtmNoscript = document.querySelector(
      'noscript iframe[src*="googletagmanager.com/ns.html"]',
    );
    if (gtmNoscript) {
      gtmNoscript.parentElement.remove();
    }

    // Clear all analytics-related global variables
    const analyticsGlobals = [
      'gtag',
      'google_tag_manager',
      '_ga',
      '_gat',
      '_gid',
      'ga',
      'gaData',
      'gaGlobal',
      'GoogleAnalyticsObject',
      'dataLayer',
      'google_analytics_uacct',
      'google_optimize',
      'google_tag_data',
      'google_tag_manager',
      'google_analytics_domain_name',
      'google_analytics_uacct',
      'google_analytics_uacct_id',
      'google_analytics_uacct_type',
      'google_analytics_uacct_version',
    ];

    analyticsGlobals.forEach((globalVar) => {
      delete window[globalVar];
    });

    // Override dataLayer to prevent any new data from being pushed

    // Clear any existing cookies
    const cookiePrefixes = [
      '_ga',
      '_gid',
      '_gat',
      '_gac_',
      '_fbp',
      '_fb',
      'AMP_TOKEN',
      '_gcl_',
    ];
    const cookieNames = [
      ...cookiePrefixes,
      ...Array.from({ length: 26 }, (_, i) =>
        Array.from(
          { length: 26 },
          (_, j) =>
            `_gcl_${String.fromCharCode(97 + i)}${String.fromCharCode(97 + j)}`,
        ),
      ).flat(),
    ];

    cookieNames.forEach((cookieName) => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
    });

    // Clear localStorage and sessionStorage
    Object.keys(localStorage).forEach((key) => {
      if (
        key.includes('_ga') ||
        key.includes('_gcl') ||
        key.includes('gtm') ||
        key.includes('analytics')
      ) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (
        key.includes('_ga') ||
        key.includes('_gcl') ||
        key.includes('gtm') ||
        key.includes('analytics')
      ) {
        sessionStorage.removeItem(key);
      }
    });
  };

  gaInstance = {
    initializeAnalytics,
    trackPageView,
    trackEvent,
    trackBookedEventEcommerce,
    trackBeginCheckoutEventEcommerce,
    trackAddPaymentInfoEventEcommerce,
    pushToDataLayer,
    pushEvent,
    clearEcommerceData,
    stopAnalytics,
  };

  return gaInstance;
};
